/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { Marker } from "react-map-gl";
import Map from "../../MapWhenReady/MapWhenReady";
import "mapbox-gl/dist/mapbox-gl.css";
import clsx from "clsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "../../../hooks";
import useMapboxAutocomplete from "../../../hooks/useMapboxAutocomplete";
import FlashMessage from "../../FlashMessage";
import useStyle from "./styles";
import MarkerImage from "../../../assets/images/marker.png";
import ClearIcon from "@mui/icons-material/Clear";

import ConfigurableValues from "../../../config/constants";

import { useTranslation } from "react-i18next";



function AddressModal({
  toggleModal,
  isVisible,
  regionDetail,
  changeAddress,
  settingRegionDetail,
  setShowDetail,
}) {
  console.log(settingRegionDetail);


  const { MAPBOX_TOKEN } = ConfigurableValues();

  const theme = useTheme();

  const classes = useStyle();
  const mapRef = useRef(null);
  const [region, setRegion] = useState(null);
  const [mainError, setMainError] = useState({});
  const [locationName, setLocationName] = useState("");
  const [inputValue, setInputValue] = useState("");
  const { options, setOptions, fetchPredictions } = useMapboxAutocomplete();
  const { t } = useTranslation();
  const { getCurrentLocation } = useLocation();
  const { latLngToGeoString } = useLocation();
  const [loading, setLoading] = useState();

  useEffect(() => {
    fetchPredictions(inputValue);
  }, [inputValue, fetchPredictions]);

  // Selecting a suggestion: Mapbox results already carry coordinates, so no
  // separate geocode round-trip is needed.
  const handleLocationSelection = (option) => {
    if (!option || option.lat == null) return;
    setLocationName(option.description);
    setInputValue(option.description);
    setOptions([]);
    setRegion({
      lat: option.lat,
      lng: option.lng,
    });
  };

  useEffect(() => {
    if (regionDetail) {
      console.log(regionDetail);
      setRegion({
        lat: regionDetail.lat,
        lng: regionDetail.lng,
      });
      setLocationName(regionDetail.location);
      setInputValue(regionDetail.location ?? "");
    }
  }, [regionDetail]);

  // Recenter the map whenever the region changes (suggestion select, current
  // location, marker drag). initialViewState only applies on first mount.
  useEffect(() => {
    if (region?.lat != null && mapRef.current) {
      mapRef.current.easeTo({
        center: [region.lng, region.lat],
        zoom: 16,
        duration: 500,
      });
    }
  }, [region]);

  const toggleSnackbar = useCallback(() => {
    setMainError({});
  }, []);

  const handleClearClick = () => {
    setLocationName("");
    setInputValue("");
  };

  // Mapbox marker drag end: event.lngLat carries the new coordinates as plain numbers.
  const changeCoordinates = async (event) => {
    const lat = event?.lngLat?.lat;
    const lng = event?.lngLat?.lng;
    if (lat == null || lng == null) return;
    setLoading(true);
    (async () => {
      const regionChange = { lat, lng };
      const geoCodeData = await latLngToGeoString({
        latitude: lat,
        longitude: lng,
      });
      setLocationName(geoCodeData);
      setInputValue(geoCodeData);
      setRegion(regionChange);
      return;
    })().finally(() => {
      setLoading(false);
    });
  };

  const submitAddress = useCallback(() => {
    if (region) {
      changeAddress((prev) => {
        return {
          ...prev,
          lat: region.lat,
          lng: region.lng,
          location: locationName,
        };
      });
    }
    toggleModal();
  }, [locationName, region]);
  const locationCallback = (error, data) => {
    setLoading(false);
    if (error) {
      setMainError({ type: "error", message: error });
      return;
    }
    setRegion({
      lat: data.latitude,
      lng: data.longitude,
    });
    setLocationName(data.deliveryAddress);
    setInputValue(data.deliveryAddress ?? "");
  };
  return (
    <>
      <FlashMessage
        open={Boolean(mainError.message)}
        severity={mainError.type}
        alertMessage={mainError.message}
        handleClose={toggleSnackbar}
      />
      <Dialog
        onClose={toggleModal}
        open={isVisible}
        scroll="body"
        fullWidth={true}
        maxWidth="sm"
        className={classes.root}
      >
        <Box display="flex" justifyContent="flex-end">
          <IconButton
            size="medium"
            onClick={() => {
              toggleModal();
              setShowDetail();
            }}
            className={classes.closeContainer}
          >
            <CloseIcon color="primary" />
          </IconButton>
        </Box>
        <DialogTitle>
          <Box component="div">
            <Typography
              variant="h5"
              color="textSecondary"
              className={clsx(classes.boldText, classes.title)}
            >
              {t("exactLocation")}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <div>
            <TextField
              variant="outlined"
              label={t("yourArea")}
              fullWidth
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setLocationName(e.target.value);
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {loading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <>
                        {locationName && (
                          <IconButton onClick={handleClearClick}>
                            <ClearIcon color="primary" />
                          </IconButton>
                        )}
                        <IconButton
                          onClick={(e) => {
                            e.preventDefault();
                            if (!loading) {
                              setLoading(true);
                              getCurrentLocation(locationCallback);
                            }
                          }}
                          size="large"
                        >
                          <GpsFixedIcon color="primary" />
                        </IconButton>
                      </>
                    )}
                  </InputAdornment>
                ),
              }}
            />
            <div>
              {options.map((option) => (
                <div
                  key={option.place_id}
                  onClick={() => handleLocationSelection(option)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    backgroundColor: theme.palette.common.white,
                    color: "black",
                    fontSize: "16px",
                    padding: "10px 16px",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      theme.palette.action.hover)
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      theme.palette.common.white)
                  }
                >
                  <LocationOnIcon color="primary" fontSize="small" />
                  {option.description}
                </div>
              ))}
            </div>
          </div>

          <Box display="flex" className={classes.mapContainer}>
            <Map
              ref={mapRef}
              mapboxAccessToken={MAPBOX_TOKEN}
              initialViewState={{
                longitude: region?.lng ?? 0,
                latitude: region?.lat ?? 0,
                zoom: 16,
              }}
              style={{ height: "400px", width: "100%" }}
              mapStyle="mapbox://styles/mapbox/streets-v12"
            >
              {region?.lat != null && (
                <Marker
                  longitude={region.lng}
                  latitude={region.lat}
                  anchor="bottom"
                  draggable={true}
                  onDragEnd={changeCoordinates}
                >
                  <img src={MarkerImage} alt="" style={{ width: 32, height: 32 }} />
                </Marker>
              )}
            </Map>
          </Box>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            disabled={loading}
            className={classes.btnBase}
            onClick={(e) => {
              e.preventDefault();
              submitAddress();
            }}
          >
            {loading ? (
              <CircularProgress color="secondary" />
            ) : (
              <Typography variant="subtitle2" className={classes.boldText}>
                {t("submit")}
              </Typography>
            )}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default React.memo(AddressModal);
